import {
  BLOCKED_COUNTRIES,
  KNOWN_CLIENT_DOMAINS,
  SUPPORTED_SYSTEMS,
  UNSUPPORTED_SYSTEMS,
  FOCUS_COUNTRIES,
  SEMI_FOCUS_COUNTRIES,
  FREE_DOMAIN_NAMES,
  GENERIC_PREFIXES,
  COUNTRY_CODES,
  type FormState,
  type LeadFacts,
  type PricingFacts,
  type ScreenKey
} from "./constants";

// ─── Email classification ─────────────────────────────────────────────────────

export function classifyEmail(
  email: string
): "work_personal" | "free_or_generic" | "unknown" {
  if (!email || !email.includes("@")) return "unknown";
  const [local, domain] = email.toLowerCase().split("@");
  if (!domain) return "unknown";
  const domainBase = domain.split(".")[0];
  if (FREE_DOMAIN_NAMES.has(domainBase)) return "free_or_generic";
  if (GENERIC_PREFIXES.has(local)) return "free_or_generic";
  return "work_personal";
}

function classifyEmailQuality(email: string): "work" | "free" | "generic" {
  if (!email || !email.includes("@")) return "generic";
  const [local, domain] = email.toLowerCase().split("@");
  if (!domain) return "generic";
  const domainBase = domain.split(".")[0];
  if (FREE_DOMAIN_NAMES.has(domainBase)) return "free";
  if (GENERIC_PREFIXES.has(local)) return "generic";
  return "work";
}

// ─── Scaffolding-contractor name detection ───────────────────────────────────
// Used to flag the "gold star" companies whose name itself signals their trade.

const SCAFFOLDING_TERMS = [
  "scaffold", // EN: scaffold, scaffolding
  "ställning", // SV
  "stillas", // NO
  "stillads", // DA
  "telineet", // FI
  "gerüst", // DE
  "geruest", // DE (transliterated)
  "steiger", // NL
  "echafaud", // FR (échafaudage)
  "andamio", // ES
  "ponteggio" // IT
];

function isScaffoldingContractor(company: string): boolean {
  if (!company) return false;
  const lower = company.toLowerCase();
  return SCAFFOLDING_TERMS.some((t) => lower.includes(t));
}

// ─── computeFacts (DemoForm) ──────────────────────────────────────────────────

export function computeFacts(state: FormState): LeadFacts {
  const iso = state.countryIso;

  const countryTier: LeadFacts["countryTier"] = BLOCKED_COUNTRIES.has(iso)
    ? "blocked"
    : FOCUS_COUNTRIES.has(iso)
      ? "focus"
      : SEMI_FOCUS_COUNTRIES.has(iso)
        ? "semi_focus"
        : "non_focus";

  // Multi-select: one Scaffcalc-supported manufacturer is enough to qualify.
  // Otherwise a known-unsupported pick routes to the waitlist as unsupported;
  // no pick (free text only) stays unknown.
  const supplierMatch: LeadFacts["supplierMatch"] = state.suppliers.some((s) =>
    SUPPORTED_SYSTEMS.has(s)
  )
    ? "supported"
    : state.suppliers.some((s) => UNSUPPORTED_SYSTEMS.has(s))
      ? "unsupported"
      : "unknown";

  const emailQuality = classifyEmailQuality(state.email);

  const domain = state.email.split("@")[1]?.toLowerCase() ?? "";
  const isExistingCustomer = domain ? KNOWN_CLIENT_DOMAINS.has(domain) : false;

  const companyType: LeadFacts["companyType"] = isScaffoldingContractor(
    state.company
  )
    ? "scaffolding_contractor"
    : "other";

  return {
    countryTier,
    supplierMatch,
    emailQuality,
    isExistingCustomer,
    companyType
  };
}

// ─── pickScreen — UI dispatcher ───────────────────────────────────────────────

export function pickScreen(facts: LeadFacts): ScreenKey {
  // Pre-checks first — these short-circuit the funnel.
  if (facts.countryTier === "blocked") return "blocked";
  if (facts.isExistingCustomer) return "existing_client";

  // Supplier check — unsupported / unknown / free-text → waitlist.
  if (facts.supplierMatch !== "supported") return "waitlist";

  // Country check — non-focus market.
  if (facts.countryTier === "non_focus") return "video_nonfocus";

  // Email quality — free / generic → secondary MQL.
  if (facts.emailQuality !== "work") return "video_focus";

  // All checks pass — top-tier lead.
  return "calendar";
}

// ─── pickMqlVariant — for HubSpot mql_variant property ───────────────────────

export type MqlVariant = "mql_high" | "mql" | "mql_referral" | "mql_low";

export function pickMqlVariant(facts: LeadFacts): MqlVariant | null {
  const screen = pickScreen(facts);
  switch (screen) {
    case "calendar":
    case "calendar_booked":
      return "mql_high";
    case "video_focus":
      return "mql";
    case "referral":
      return "mql_referral";
    case "video_nonfocus":
      return "mql_low";
    case "waitlist":
    case "blocked":
    case "existing_client":
      return null;
  }
}

// ─── pickPriority — for HubSpot priority property ────────────────────────────

export type Priority = "high" | "medium" | "low";

export function pickPriority(facts: LeadFacts): Priority | null {
  const variant = pickMqlVariant(facts);
  switch (variant) {
    case "mql_high":
      return "high";
    case "mql":
    case "mql_referral":
      return "medium";
    case "mql_low":
      return "low";
    case null:
      return null;
  }
}

// ─── pickLifecycleStage — for HubSpot lifecycleStage ─────────────────────────

export type LifecycleStage = "MQL" | "IQL" | "out_of_funnel";

export function pickLifecycleStage(facts: LeadFacts): LifecycleStage {
  const screen = pickScreen(facts);
  if (screen === "blocked" || screen === "existing_client") {
    return "out_of_funnel";
  }
  if (screen === "waitlist") return "IQL";
  return "MQL";
}

// ─── pickLoopsTags — for Loops contact userGroup + tags ──────────────────────

export function pickLoopsTags(facts: LeadFacts): string[] {
  const screen = pickScreen(facts);
  const variant = pickMqlVariant(facts);
  const priority = pickPriority(facts);

  const tags: string[] = [`screen:${screen}`];
  if (variant) tags.push(`mql_variant:${variant}`);
  if (priority) tags.push(`priority:${priority}`);
  tags.push(`country_tier:${facts.countryTier}`);
  tags.push(`supplier_match:${facts.supplierMatch}`);
  tags.push(`email_quality:${facts.emailQuality}`);
  if (facts.companyType === "scaffolding_contractor") {
    tags.push("company_type:scaffolding_contractor");
  }
  return tags;
}

// ─── computePricingFacts (PricingForm) ────────────────────────────────────────

interface PricingState {
  need: string;
  volume: string;
  team: string;
  contactMethod: "phone" | "email" | null;
  email: string;
}

export function computePricingFacts(state: PricingState): PricingFacts {
  const enterpriseTrigger =
    state.volume === "over_300" || state.team === "large";

  return {
    needType: (state.need || "quotation") as PricingFacts["needType"],
    volumeTier: (state.volume || "under_20") as PricingFacts["volumeTier"],
    teamSize: (state.team || "solo") as PricingFacts["teamSize"],
    contactMethod: state.contactMethod ?? "email",
    emailQuality:
      state.contactMethod === "email"
        ? classifyEmailQuality(state.email)
        : null,
    enterpriseTrigger
  };
}

// ─── PricingForm derivers ─────────────────────────────────────────────────────

export type SuggestedPlan = "go" | "pro" | "expert" | "enterprise";

export function pickSuggestedPlan(facts: PricingFacts): SuggestedPlan {
  if (facts.enterpriseTrigger) return "enterprise";
  if (facts.needType === "engineering" || facts.needType === "all")
    return "expert";
  if (facts.needType === "planning")
    return facts.volumeTier === "100_to_300" ? "expert" : "pro";
  if (facts.volumeTier === "100_to_300") return "expert";
  if (facts.volumeTier === "20_to_100") return "pro";
  return facts.teamSize === "solo" ? "go" : "pro";
}

export type Track = "standard" | "custom_proposal";

export function pickTrack(facts: PricingFacts): Track {
  return facts.enterpriseTrigger ? "custom_proposal" : "standard";
}

export function pickPricingLoopsTags(facts: PricingFacts): string[] {
  const plan = pickSuggestedPlan(facts);
  const track = pickTrack(facts);
  const tags = [
    `plan:${plan}`,
    `track:${track}`,
    `need:${facts.needType}`,
    `volume:${facts.volumeTier}`,
    `team:${facts.teamSize}`,
    `contact_method:${facts.contactMethod}`
  ];
  if (facts.emailQuality) tags.push(`email_quality:${facts.emailQuality}`);
  return tags;
}

// ─── Email name/company parsing ───────────────────────────────────────────────

const PARSER_GENERIC_PREFIXES = new Set([
  "info",
  "admin",
  "office",
  "sales",
  "contact",
  "post",
  "procurement",
  "reception",
  "booking",
  "accounts",
  "enquiries",
  "support",
  "service",
  "quotes",
  "tenders",
  "general",
  "help",
  "billing",
  "invoice",
  "hello",
  "noreply",
  "no-reply",
  "newsletter",
  "marketing",
  "hr"
]);

const COMPANY_SUFFIX_WORDS = new Set([
  "oy",
  "kb",
  "hb",
  "ab",
  "as",
  "asa",
  "bv",
  "nv"
]);

function toTitleCase(s: string) {
  return s
    .split("-")
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join("-");
}

function isAlphaOnly(s: string) {
  return /^[a-zA-ZÀ-ÖØ-öø-ÿ]+(-[a-zA-ZÀ-ÖØ-öø-ÿ]+)*$/.test(s);
}

export function parseEmailHints(email: string): {
  firstName: string;
  lastName: string;
  company: string;
} {
  const result = { firstName: "", lastName: "", company: "" };
  if (!email || !email.includes("@")) return result;
  const [local, domain] = email.toLowerCase().split("@");
  if (!domain) return result;

  // Company from domain base
  const domainBase = domain.split(".")[0];
  if (domainBase && !FREE_DOMAIN_NAMES.has(domainBase)) {
    result.company = domainBase
      .replace(/-/g, " ")
      .split(" ")
      .map((w) =>
        COMPANY_SUFFIX_WORDS.has(w)
          ? ""
          : w.charAt(0).toUpperCase() + w.slice(1)
      )
      .filter(Boolean)
      .join(" ");
  }

  if (PARSER_GENERIC_PREFIXES.has(local)) return result;

  // Separator-based name parsing
  const sep = local.match(/^([^._+]+)[._+]([^._+]+)/);
  if (sep) {
    const [, first, second] = sep;
    if (first.length >= 2 && isAlphaOnly(first)) {
      result.firstName = toTitleCase(first);
      if (second.length >= 2 && isAlphaOnly(second)) {
        result.lastName = toTitleCase(second);
      }
    }
    return result;
  }

  // No separator — use whole local as first name if short
  if (local.length >= 3 && local.length <= 12 && isAlphaOnly(local)) {
    result.firstName = toTitleCase(local);
  }

  return result;
}

// ─── Country code inference from email TLD / company suffix ──────────────────

const TLD_TO_CODE: Record<string, string> = {
  se: "+46",
  no: "+47",
  dk: "+45",
  fi: "+358",
  de: "+49",
  at: "+43",
  ch: "+41",
  nl: "+31",
  be: "+32",
  au: "+61",
  uk: "+44",
  fr: "+33",
  it: "+39",
  es: "+34",
  pl: "+48",
  cz: "+420",
  sk: "+421",
  hu: "+36",
  ro: "+40",
  pt: "+351",
  gr: "+30",
  hr: "+385",
  rs: "+381",
  ee: "+372",
  lv: "+371",
  lt: "+370",
  in: "+91",
  cn: "+86",
  jp: "+81",
  kr: "+82",
  sg: "+65",
  nz: "+64",
  za: "+27",
  br: "+55",
  mx: "+52"
};

export function inferCountryCode(
  email: string,
  _company: string
): string | null {
  if (!email.includes("@")) return null;
  const domain = email.split("@")[1]?.toLowerCase() ?? "";
  if (domain.endsWith(".co.uk")) return "+44";
  const tld = domain.split(".").pop() ?? "";
  return TLD_TO_CODE[tld] ?? null;
}

export function inferCountryIso(dialCode: string): string {
  const match = COUNTRY_CODES.find((c) => c.code === dialCode && c.focus);
  return match?.iso ?? COUNTRY_CODES[0].iso;
}
